/**
 * THE NAVIGATION MODEL — what each destination IS, and what kind of thing it is.
 *
 * The nav used to be SEVEN FLAT PEERS that were four different kinds of thing: an action
 * (New run), a collection (Library), two per-run stages (Editor, Review), and three
 * reference surfaces (Pipeline, Settings, Help). Presenting them as peers told the operator
 * they were equivalent destinations. They are not, and the owner — who built the thing —
 * could not say what his own tabs pointed at.
 *
 * So the model now carries the two facts the chrome was missing:
 *
 *   1. KIND. Work / This run / Reference. The group is rendered, with its label, in every
 *      surface that renders the nav: the header row at >=768, and the menu sheet that IS
 *      the nav on a phone.
 *   2. WHAT IT SHOWS. One line, plain English, for an operator — not a feature name. It is
 *      printed under the item in the menu sheet, and in the header's caption line as the
 *      pointer or the keyboard moves across the row.
 *
 * The run STAGES are not in this file: they are not fixed destinations, they belong to a
 * run and their state is derived from that run. See lib/runFlow.ts.
 */

export type NavGroupId = "work" | "run" | "reference";

export type NavDestination = {
  id: string;
  /** What the item is called. This is also its data-nav test hook. */
  label: string;
  to: string;
  /** react-router `end` — only exact matches count as active. */
  end?: boolean;
  /** ONE line. What this destination actually shows, said to an operator. */
  description: string;
};

export const NAV_GROUPS: Record<NavGroupId, { label: string; meaning: string }> = {
  work: {
    label: "Work",
    meaning: "Start a document, or find one you already made."
  },
  run: {
    label: "This run",
    meaning: "The run you have open, and the stage it has reached."
  },
  reference: {
    label: "Reference",
    meaning: "How ATLAS is set up and what it checks."
  }
};

/** ACTIONS AND COLLECTIONS. These exist whether or not a run is open. */
export const WORK_ITEMS: NavDestination[] = [
  {
    id: "new",
    label: "New run",
    to: "/new",
    end: true,
    description: "Start a document for one funder."
  },
  {
    id: "library",
    label: "Library",
    to: "/runs",
    end: true,
    description: "Every document you have generated."
  }
];

/** REFERENCE. Nothing here belongs to a run; nothing here changes a run. */
export const REFERENCE_ITEMS: NavDestination[] = [
  {
    id: "pipeline",
    label: "Pipeline",
    to: "/pipeline",
    description: "The six checks every export must pass."
  },
  {
    id: "settings",
    label: "Settings",
    to: "/settings",
    description: "The source document, the API connection and the MCP token."
  },
  {
    id: "help",
    label: "Help",
    to: "/help",
    description: "How to connect an AI tool to ATLAS."
  }
];

/** What the "This run" group says when there is no run open. It offers the two real next
 *  steps and it does NOT quietly open the most recent run, which is what it used to do. */
export const NO_RUN_OPEN = {
  label: "No run open",
  description: "Draft and Review belong to one run. Start a run, or open one from the Library."
};
